import React, { useState } from 'react';
import { Box, Button, Paper, Typography, Alert, Chip } from '@mui/material';
import {
    DndContext,
    DragEndEvent,
    DragOverlay,
    DragStartEvent,
    PointerSensor,
    useSensor,
    useSensors,
    useDroppable,
    useDraggable,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RichTextContent from '../../common/RichTextContent';
import { shuffle } from '../../../utils/shuffle';

interface DragDropPlayerProps {
    lesson: any;
    onComplete: () => void;
    onProgressUpdate?: (lessonId: number, progressData: {
        status?: string;
        progressPercentage?: number;
        score?: string;
        timeSpentSeconds?: number;
        suspendData?: any;
    }) => Promise<boolean>;
}

const BANK_ID = '__bank__';

const DraggableItem: React.FC<{
    id: string;
    text: string;
    submitted: boolean;
    isCorrect?: boolean;
    disabled?: boolean;
}> = ({ id, text, submitted, isCorrect, disabled }) => {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id,
        disabled: disabled || submitted,
    });

    return (
        <Box
            ref={setNodeRef}
            {...listeners}
            {...attributes}
            sx={{
                px: 2,
                py: 1,
                borderRadius: 1,
                border: 2,
                borderColor: submitted
                    ? isCorrect ? 'success.main' : 'error.main'
                    : 'primary.main',
                bgcolor: submitted
                    ? isCorrect ? 'success.light' : 'error.light'
                    : 'background.paper',
                cursor: submitted ? 'default' : isDragging ? 'grabbing' : 'grab',
                opacity: isDragging ? 0 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                userSelect: 'none',
                boxShadow: 1,
                transform: CSS.Translate.toString(transform),
            }}
        >
            <Typography variant="body2">{text}</Typography>
            {submitted && (isCorrect
                ? <CheckCircleIcon fontSize="small" color="success" />
                : <CancelIcon fontSize="small" color="error" />
            )}
        </Box>
    );
};

const DroppableZone: React.FC<{
    id: string;
    label: string;
    children: React.ReactNode;
    isOver: boolean;
}> = ({ id, label, children, isOver }) => {
    const { setNodeRef } = useDroppable({ id });

    return (
        <Box
            ref={setNodeRef}
            sx={{
                flex: 1,
                minWidth: 180,
                minHeight: 100,
                border: 2,
                borderStyle: 'dashed',
                borderColor: isOver ? 'primary.main' : 'divider',
                borderRadius: 2,
                p: 2,
                bgcolor: isOver ? 'primary.50' : 'grey.50',
                transition: 'border-color 0.15s, background-color 0.15s',
            }}
        >
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                {label}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, minHeight: 40 }}>
                {children}
            </Box>
        </Box>
    );
};

const DroppableBank: React.FC<{ children: React.ReactNode; isOver: boolean }> = ({ children, isOver }) => {
    const { setNodeRef } = useDroppable({ id: BANK_ID });

    return (
        <Box
            ref={setNodeRef}
            sx={{
                p: 2,
                mb: 3,
                border: 2,
                borderStyle: 'dashed',
                borderColor: isOver ? 'primary.main' : 'divider',
                borderRadius: 2,
                bgcolor: isOver ? 'primary.50' : 'background.paper',
                minHeight: 60,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                alignItems: 'flex-start',
                transition: 'border-color 0.15s, background-color 0.15s',
            }}
        >
            {children}
        </Box>
    );
};

const DragDropPlayer: React.FC<DragDropPlayerProps> = ({ lesson, onComplete, onProgressUpdate }) => {
    const content = lesson.content || {};
    const { instruction, items = [], zones = [] } = content;

    const itemKey = items.map((item: any) => `${item.id}:${item.text}`).join('|');

    // itemId → zoneId, undefined = in bank
    const [placements, setPlacements] = useState<Record<string, string>>({});
    const [bank, setBank] = useState(() => ({ key: itemKey, items: shuffle<any>(items) }));
    const [submitted, setSubmitted] = useState(false);
    const [activeId, setActiveId] = useState<string | null>(null);
    const [overId, setOverId] = useState<string | null>(null);

    if (bank.key !== itemKey) {
        setBank({ key: itemKey, items: shuffle<any>(items) });
    }

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const handleDragStart = (event: DragStartEvent) => {
        setActiveId(String(event.active.id));
    };

    const handleDragOver = (event: any) => {
        setOverId(event.over ? String(event.over.id) : null);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        setActiveId(null);
        setOverId(null);
        const { active, over } = event;
        if (!over) return;

        const itemId = String(active.id);
        const targetId = String(over.id);

        setPlacements(prev => {
            const next = { ...prev };
            if (targetId === BANK_ID) {
                delete next[itemId];
            } else {
                next[itemId] = targetId;
            }
            return next;
        });
    };

    const getItemsInZone = (zoneId: string) =>
        items.filter((item: any) => placements[item.id] === zoneId);

    const getUnplacedItems = () =>
        bank.items.filter((item: any) => !placements[item.id]);

    const checkAnswers = () => {
        let correct = 0;
        items.forEach((item: any) => {
            if (placements[item.id] === item.correctZone) correct++;
        });
        return Math.round((correct / items.length) * 100);
    };

    const score = submitted ? checkAnswers() : 0;
    const passed = score >= (lesson.minScore || 70);
    const activeItem = activeId ? items.find((i: any) => i.id === activeId) : null;
    const allPlaced = items.length > 0 && getUnplacedItems().length === 0;

    // Issue #332: Score ans Backend melden, bevor die Lektion abgeschlossen wird.
    const handleComplete = async () => {
        if (onProgressUpdate) {
            await onProgressUpdate(lesson.id, {
                score: String(score),
                status: 'completed',
                progressPercentage: 100,
            });
        }
        onComplete();
    };

    return (
        <Box>
            <Paper sx={{ p: 3, mb: 3 }}>
                <RichTextContent
                    html={instruction || 'Ordnen Sie die Elemente den richtigen Bereichen zu'}
                    sx={{ mb: 2 }}
                />

                <DndContext
                    sensors={sensors}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDragEnd={handleDragEnd}
                >
                    {/* Item Bank */}
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                        Verfügbare Elemente
                    </Typography>
                    <DroppableBank isOver={overId === BANK_ID}>
                        {getUnplacedItems().length === 0 && !submitted && (
                            <Typography variant="body2" color="text.disabled" sx={{ alignSelf: 'center' }}>
                                Alle Elemente platziert
                            </Typography>
                        )}
                        {getUnplacedItems().map((item: any) => (
                            <DraggableItem
                                key={item.id}
                                id={item.id}
                                text={item.text}
                                submitted={submitted}
                            />
                        ))}
                        {submitted && getUnplacedItems().map((item: any) => (
                            <DraggableItem
                                key={item.id}
                                id={item.id}
                                text={item.text}
                                submitted={true}
                                isCorrect={false}
                            />
                        ))}
                    </DroppableBank>

                    {/* Drop Zones */}
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        {zones.map((zone: any) => (
                            <DroppableZone
                                key={zone.id}
                                id={zone.id}
                                label={zone.label}
                                isOver={overId === zone.id}
                            >
                                {getItemsInZone(zone.id).map((item: any) => (
                                    <DraggableItem
                                        key={item.id}
                                        id={item.id}
                                        text={item.text}
                                        submitted={submitted}
                                        isCorrect={submitted && item.correctZone === zone.id}
                                    />
                                ))}
                            </DroppableZone>
                        ))}
                    </Box>

                    <DragOverlay>
                        {activeItem && (
                            <Box sx={{
                                px: 2, py: 1, borderRadius: 1, border: 2,
                                borderColor: 'primary.main', bgcolor: 'background.paper',
                                boxShadow: 4, cursor: 'grabbing',
                            }}>
                                <Typography variant="body2">{activeItem.text}</Typography>
                            </Box>
                        )}
                    </DragOverlay>
                </DndContext>

                {submitted && (
                    <Alert severity={passed ? 'success' : 'error'} sx={{ mt: 3 }}>
                        <Typography variant="subtitle2">
                            Ergebnis: {score}%
                        </Typography>
                        {passed
                            ? `Gut gemacht! Sie haben ${score}% erreicht und damit bestanden.`
                            : `Sie haben ${score}% erreicht. Sie benötigen ${lesson.minScore || 70}% zum Bestehen.`}
                    </Alert>
                )}
            </Paper>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                {!submitted ? (
                    <Button
                        variant="contained"
                        onClick={() => setSubmitted(true)}
                        disabled={!allPlaced}
                    >
                        Absenden
                    </Button>
                ) : passed ? (
                    <Button variant="contained" onClick={handleComplete}>Bestätigen</Button>
                ) : (
                    <Button variant="outlined" onClick={() => { setPlacements({}); setSubmitted(false); }}>
                        Erneut versuchen
                    </Button>
                )}
            </Box>
        </Box>
    );
};

export default DragDropPlayer;
